import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import { request } from '@/request';
import { showMessage, showNotification } from '@/utils/notificationHelper';
import moment from 'moment';

const AttendanceContext = createContext();

export const AttendanceProvider = ({ children }) => {
    const { isLoggedIn } = useSelector(selectAuth);
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [requiredProductiveMs, setRequiredProductiveMs] = useState(27900000); // 7h 45m default
    const [status, setStatus] = useState('out'); // 'in', 'out', 'loading'

    const timerRef = useRef(null);

    const getManualTasksDurationMs = (att) => {
        if (!att?.dailyTask) return 0;
        return att.dailyTask.reduce((acc, task) => {
            if (task.isManual) {
                const match = task.duration?.match(/(?:(\d+)h\s*)?(?:(\d+)m\s*)?(?:(\d+)s)?/);
                if (match) {
                    const hh = parseInt(match[1] || 0);
                    const mm = parseInt(match[2] || 0);
                    const ss = parseInt(match[3] || 0);
                    return acc + (hh * 3600 + mm * 60 + ss) * 1000;
                }
            }
            return acc;
        }, 0);
    };

    const calculateElapsed = useCallback((att) => {
        if (!att) return 0;

        const isManagement = att.trackingType === 'management';

        if (isManagement) {
            // For management, progress = (Current Time - Mark In - Breaks) + Manual Task Durations
            const timeIn = att.timestamps?.[0]?.timeIn || att.timeIn;
            if (!timeIn) return getManualTasksDurationMs(att);

            let currentBreak = 0;
            if (att.isBreaking && att.currentBreakStart) {
                currentBreak = moment().diff(moment(att.currentBreakStart));
            }
            const actualBreakMs = (att.totalBreakMs || 0) + Math.max(0, currentBreak);
            const clockElapsed = moment().diff(moment(timeIn));
            const manualTaskMs = getManualTasksDurationMs(att);
            
            return Math.max(0, clockElapsed - actualBreakMs + manualTaskMs);
        }

        let total = att.totalProductiveMs || 0;

        // For productivity users, progress ONLY accumulates when a task is running.
        if (att.activeTaskId && att.dailyTask) {
            const activeTask = att.dailyTask.find(t => String(t._id) === String(att.activeTaskId));
            if (activeTask && activeTask.taskHistory?.length > 0) {
                const lastAction = activeTask.taskHistory[activeTask.taskHistory.length - 1];
                if (lastAction.action === 'start' && lastAction.time) {
                    total += moment().diff(moment(lastAction.time));
                }
            }
        }

        return total;
    }, []);

    const refreshSession = useCallback(async () => {
        if (!isLoggedIn) return;

        try {
            const data = await request.get({ entity: 'timein/active-session' });
            if (data.success && data.activeSession) {
                const session = data.activeSession;
                if (session.timestamps && session.timestamps.length > 0) {
                    session.timeIn = session.timestamps[0].timeIn;
                    session.timeOut = session.timestamps[0].timeOut;
                }
                setAttendance(session);
                setRequiredProductiveMs(session.requiredProductiveMs || 27900000);

                // WFH Continuation: if the employee confirmed WFH but hasn't marked in from home yet,
                // show Mark Time In so they explicitly resume from home.
                // Condition: continuationStartedAt is set but wfhStartedAt is NOT yet set.
                const wfhPending = localStorage.getItem('wfhContinuationPending') === 'true';
                const isWfhPendingMarkIn = session.wfh?.continuationStartedAt && !session.wfh?.wfhStartedAt;

                if (wfhPending) {
                    localStorage.removeItem('wfhContinuationPending');
                }

                if (isWfhPendingMarkIn) {
                    setStatus('out'); // Show Mark Time In button — employee hasn't resumed from home yet
                    setElapsedMs(0);
                } else {
                    setStatus('in');
                    setElapsedMs(calculateElapsed(session));
                }
                return session;
            } else {
                setAttendance(null);
                setStatus('out');
                setElapsedMs(0);
                return null;
            }
        } catch (error) {
            console.error('Attendance sync error:', error);
            return null;
        } finally {
            setLoading(false);
        }
    }, [isLoggedIn, calculateElapsed]);

    useEffect(() => {
        refreshSession();
    }, [refreshSession]);

    useEffect(() => {
        const trackingType = attendance?.trackingType || 'productivity';
        const isManagement = trackingType === 'management';

        if (status === 'in' && !attendance?.isBreaking) {
            // Recalculate from source every second so manual tasks AND running tasks
            // are immediately reflected in the live counter. 
            // This prevents "timer lag" when the tab is in the background.
            timerRef.current = setInterval(() => {
                setElapsedMs(calculateElapsed(attendance));
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [status, attendance?.isBreaking, attendance?.activeTaskId, attendance?.trackingType, attendance, calculateElapsed]);

    const clockIn = async () => {
        try {
            // WFH Resume: if there's already an open session with WFH continuation,
            // don't call the backend mark-in endpoint — just resume the existing session
            if (attendance?.wfh?.continuationStartedAt && !attendance?.isClosed) {
                // Stamp the WFH start time on the backend
                try {
                    await request.post({ entity: 'attendance/wfh-markin' });
                } catch (e) {
                    console.warn('[clockIn] WFH mark-in stamp failed:', e);
                }
                // Refresh to get updated session with wfhStartedAt
                const updatedSession = await refreshSession();
                showNotification.success('Welcome Home! 🏠', 'Resuming your session from home.');
                setStatus('in');
                if (updatedSession) setElapsedMs(calculateElapsed(updatedSession));
                return { success: true };
            }

            const data = await request.post({ entity: 'attendance/session/start' });
            if (data.success) {
                showNotification.success('Checked In', 'Session started successfully.');
                await refreshSession();
                return data; // Return data for caller
            }
        } catch (error) {
            showNotification.error('Check-in Failed', error.response?.data?.message || 'Server error');
            return { success: false, error };
        }
    };

    const toggleBreak = async () => {
        try {
            const data = await request.post({ entity: 'attendance/break/toggle' });
            if (data.success) {
                showNotification.info(data.attendance?.isBreaking ? 'Break Started' : 'Break Ended', data.attendance?.isBreaking ? 'Your timer is paused.' : 'Welcome back!');
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Error', 'Failed to toggle break status.');
            return { success: false, error };
        }
    };

    const startTask = async (description, asanaInfo = null) => {
        try {
            const data = await request.post({
                entity: 'attendance/task/start',
                jsonData: {
                    taskId: asanaInfo?._id || null,
                    description,
                    taskGid: asanaInfo?.taskGid || null,
                    parent: asanaInfo?.parent || null,
                    customField: asanaInfo?.customField || null,
                    customFieldTwo: asanaInfo?.customFieldTwo || null,
                    estimatedField: asanaInfo?.estimatedField || null,
                    AllotedHours: asanaInfo?.AllotedHours || 0,
                    totalEffortTime: asanaInfo?.totalEffortTime || 0,
                    utilizedField: asanaInfo?.utilizedField || null,
                    extraHourTask: asanaInfo?.extraHourTask || false,
                    sectionGid: asanaInfo?.sectionGid || null,
                    sectionName: asanaInfo?.sectionName || null,
                    estimatedTime: asanaInfo?.estimatedTime || 0
                }
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Task Error', error.response?.data?.message || 'Failed to start task');
            return { success: false, error };
        }
    };

    const stopTask = async (asanaInfo = null) => {
        try {
            const data = await request.patch({
                entity: 'attendance/task/stop',
                jsonData: { task: asanaInfo }
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Task Error', 'Failed to stop task');
            return { success: false, error };
        }
    };

    const completeTask = async (taskId, completionData) => {
        try {
            const data = await request.patch({
                entity: `attendance/task/complete/${taskId}`,
                jsonData: completionData
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Task Error', error.response?.data?.message || 'Failed to complete task');
            return { success: false, error };
        }
    };

    const addManualTaskBulk = async (tasksArray) => {
        try {
            const data = await request.post({
                entity: 'attendance/task/manual-bulk',
                jsonData: { tasks: tasksArray }
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Task Error', error.response?.data?.message || 'Failed to add tasks in bulk');
            return { success: false, error };
        }
    };

    const editManualTask = async (taskId, updates) => {
        try {
            const data = await request.patch({
                entity: `attendance/task/edit/${taskId}`,
                jsonData: updates
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Error', error.response?.data?.message || 'Failed to edit task');
            return { success: false, error };
        }
    };

    const deleteTask = async (taskId) => {
        try {
            const data = await request.delete({
                entity: `attendance/task`,
                id: taskId
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Error', error.response?.data?.message || 'Failed to delete task');
            return { success: false, error };
        }
    };

    const finalizeSession = async (payload) => {
        try {
            const data = await request.patch({
                entity: 'attendance/session/finalize',
                jsonData: payload
            });
            // Backend returns { message, attendanceRecord } on success, not { success: true }
            if (data.attendanceRecord || data.message?.includes('successfully')) {
                showNotification.success('Shift Ended', 'Attendance marked successfully.');
                setAttendance(null);
                setStatus('out');
                setElapsedMs(0);
                return { success: true, ...data };
            }
            return { success: false, error: data };
        } catch (error) {
            showNotification.error('Error', 'Failed to finalize session');
            return { success: false, error };
        }
    };

    const deleteTasksBulk = async (taskIds) => {
        try {
            const data = await request.post({
                entity: 'attendance/task/delete-bulk',
                jsonData: { taskIds }
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Task Error', 'Failed to delete tasks');
            return { success: false, error };
        }
    };

    const endOvertime = async () => {
        try {
            const data = await request.patch({
                entity: 'attendance/overtime/end'
            });
            if (data.success) {
                await refreshSession();
                return data;
            }
        } catch (error) {
            showNotification.error('Overtime Error', 'Failed to end overtime session');
            return { success: false, error };
        }
    };

    return (
        <AttendanceContext.Provider value={{
            attendance,
            loading,
            status,
            elapsedMs,
            requiredProductiveMs,
            refreshSession,
            clockIn,
            toggleBreak,
            startTask,
            stopTask,
            completeTask,
            addManualTaskBulk,
            editManualTask,
            deleteTask,
            deleteTasksBulk,
            finalizeSession,
            endOvertime
        }}>
            {children}
        </AttendanceContext.Provider>
    );
};

export const useAttendance = () => useContext(AttendanceContext);
